// This is the main runner for injected code
// We sort out what injector needs to be run based on the URL

import {
  DOMMessage,
  DOMMessageResponse,
  ErrorDOMResponse,
  QuestionsResponse,
  GenericDOMResponse
} from '../types';
import {
  gFormsGetQuestions,
  gFormsAnswerQuestions,
  gFormsAnswerQuestion
} from './GoogleFormInject.ts';
import * as PromptInject from './PromptInject.ts';
import * as NewPromptInject from './NewPromptInject.ts';
import * as JaneInject from './JaneInject.ts';
import * as MWInject from './MWInject.ts';
import * as WebPTInject from './WebPTInject.ts';
import * as NewWebPTInject from './NewWebPTInject.ts';
import * as WebPTSoap2Inject from './WebPTSoap2Inject.ts';
import * as NewWebPTSoap2Inject from './NewWebPTSoap2Inject.ts';
import * as WebPTSoap2InjectChromeFreezeFix from './WebPTSoap2InjectChromeFreezeFix.ts';
import * as PTEverywhereInject from './PTEverywhereInject.ts';
import * as FusionInject from './FusionInject.ts';
import * as AthenaInject from './AthenaInject.ts';
import * as PracticePerfectInject from './PracticePerfectInject.ts';
import * as PracticeProInject from './PracticeProInject.ts';
import * as ClincientInject from './ClinicientInject.ts';
import * as HenoInject from './HenoInject.ts';
import * as TheraOfficeInject from './TheraOfficeInject.ts';
import * as RaintreeInject from './RaintreeInject.ts';
import * as NewRaintreeInject from './NewRaintreeInject.ts';
import * as PlayabilitiesRaintreeInject from './PlayabilitiesRaintreeInject.ts';
import * as CoraHealthInject from './CoreHealthInject.ts';
import * as NewCoraHealthInject from './NewCoraHealthInject.ts';
import * as StrideTheraInject from './StrideTheraInject.ts';
import * as HelloNoteInject from './HelloNoteInject.ts';
import * as NetHealthInject from './NetHealthInject.ts';
import * as CentralReachInject from './CentralReachInject.ts';
import * as IntakeQInject from './IntakeQInject.ts';
import * as TherabillInject from './TherabillInject.ts';
import * as EmergeInject from './EmergeInject.ts';
import * as CompriseHealthInject from './CompriseHealthInject.ts';
import * as KinserWellskyInject from './KinserWellskyInject.ts';
import * as CharmHealthInject from './CharmHealthInject.ts';
import * as LeadCaptureInject from './LeadCaptureInject.ts';
import { initEMRBanner } from './EMRBanner.ts';
import { initComplianceRail, mountEmbedComplianceRail, type RailHost } from './ComplianceRail.ts';
import {
  drainEmrReplayEvents,
  requestEmrReplayCheckout,
  startEmrReplayRecording
} from './EmrReplayRecorder.ts';
import {
  normalizePlaygroundPageInfo,
  normalizePlaygroundPageUrl
} from './PlaygroundURLOverrides.ts';
import {
  captureNoteIdentity,
  freezeInitialNote,
  initNoteCapture,
  linkNoteToConversation,
  setNoteCaptureEnabled
} from './NoteCapture.ts';
import { syncFocusNoteCapture } from './NoteFocusCapture.ts';
// Import webFloating functionality directly
// import '../contentScripts/webFloating.js';

const STALE_CONTEXT_MESSAGE = '__STALE_CONTEXT__';
const EXTENSION_SESSION_KEY = 'comprehendpt_extension_session';
let featureFlags: Record<string, any> = {};
let featureFlagsPushed = false;

declare global {
  interface Window {
    currentInjector?: any;
    ComprehendEmbed?: { send: (type: string, payload: any) => any };
  }
}
var gFormsInjector = {
  getQuestions: gFormsGetQuestions,
  answerQuestions: gFormsAnswerQuestions
};

const injectors = {
  'https://docs.google.com/forms': [gFormsInjector],
  'go.promptemr.com': PromptInject,
  janeapp: JaneInject,
  'mwtherapy.com': MWInject,
  'empoweremr.com': MWInject,
  'app.webpt.com': WebPTInject,
  'emr.webpt.com': WebPTSoap2Inject,
  'app.pteverywhere.com': PTEverywhereInject,
  'app.fusionwebclinic.com': FusionInject,
  'athenahealth.com': AthenaInject,
  'app.practiceperfectemr.com': PracticePerfectInject,
  'ptpracticepro.com': PracticeProInject,
  'insight.clinicient.com': ClincientInject,
  'sandbox.raintreeinc.com': RaintreeInject,
  'playabilities.raintreeinc.com': PlayabilitiesRaintreeInject,
  'raintreeinc.com': RaintreeInject,
  'https://heno': HenoInject,
  theraofficeweb: TheraOfficeInject,
  'corahealth.com': CoraHealthInject,
  'stridethera.com': StrideTheraInject,
  'hellonote.com': HelloNoteInject,
  'casamba.net': NetHealthInject,
  'centralreach.com': CentralReachInject,
  'intakeq.com': IntakeQInject,
  'therabill.com': TherabillInject,
  'ehr.software': EmergeInject,
  'kinnser.net': KinserWellskyInject,
  'u2vtbdkgke.us-east-1.awsapprunner.com': CompriseHealthInject,
  'emr.comprisehealth.net': CompriseHealthInject,
  'charmtracker.com': CharmHealthInject,
  // Comprehend's own booth lead form (any host; the page name is the key)
  'comprehend-booth-capture': LeadCaptureInject
};

let _cachedRaintreeInjector:
  | typeof RaintreeInject
  | typeof NewRaintreeInject
  | null = null;

let _cachedWebPTSoap2Injector:
  | typeof WebPTSoap2Inject
  | typeof NewWebPTSoap2Inject
  | typeof WebPTSoap2InjectChromeFreezeFix
  | null = null;

// The July 20 rollback wins if both flags are on.
function pickWebPTSoap2Injector(newIntegration: boolean, freezeFix: boolean) {
  if (newIntegration) return NewWebPTSoap2Inject;
  if (freezeFix) return WebPTSoap2InjectChromeFreezeFix;
  return WebPTSoap2Inject;
}

// WebPT 1.0 reads its flag from the pushed/stored features, so no GET_DOM
// arg plumbing is needed through the three react_webInjector copies.
function pickWebPTInjector() {
  return featureFlags.new_webpt_integration === true
    ? NewWebPTInject
    : WebPTInject;
}

// Pinned at scrape so a flag push before PUSH_ANSWER can't swap injectors.
let _cachedWebPTInjector: typeof WebPTInject | typeof NewWebPTInject | null =
  null;

let _cachedPromptInjector:
  | typeof PromptInject
  | typeof NewPromptInject
  | null = null;

let _cachedCoraInjector:
  | typeof CoraHealthInject
  | typeof NewCoraHealthInject
  | null = null;


export function lookupInjectorKey() {
  // Replayed EMRs run in about:blank, so the replay viewer supplies the
  // original EMR URL for injector matching.
  const replayUrl = (window as any).__cptCaptureUrl;
  const urlForMatch = replayUrl || document.URL;
  let hostForMatch = window.location.hostname;
  if (replayUrl) {
    try {
      hostForMatch = new URL(replayUrl).hostname;
    } catch (e) {
      // Keep the current hostname if the replay URL is invalid.
    }
  }

  for (var key in injectors) {
    if (key === 'corahealth.com') {
      const hostname = hostForMatch;
      if (hostname === key || hostname.endsWith(`.${key}`)) return key;
      continue;
    }
    if (urlForMatch.includes(key)) {
      // console.log("Found a match for the injector", key)
      return key;
    }
  }
  // console.log("No injector found for this URL");
  return null;
}

/** Returns the injector for the current document.URL (for CLI / CDP use). */
export function getInjector() {
  const key = lookupInjectorKey();
  if (!key) return null;
  if (key === 'corahealth.com') return _cachedCoraInjector ?? CoraHealthInject;
  // The note poller captures through the user's integration flag, the one
  // Comprehend uses. A GET_DOM from elsewhere (Template Builder sends no flag)
  // must not switch the note's paths, so its cached choice is only a fallback.
  if (key === 'sandbox.raintreeinc.com' || key === 'raintreeinc.com') {
    if ('new_rt_integration' in featureFlags) {
      return featureFlags.new_rt_integration === true
        ? NewRaintreeInject
        : RaintreeInject;
    }
    return _cachedRaintreeInjector ?? RaintreeInject;
  }
  if (key === 'emr.webpt.com') {
    if (
      'new_webpt_soap2_integration' in featureFlags ||
      'webpt2_chrome_freeze_fix' in featureFlags
    ) {
      return pickWebPTSoap2Injector(
        featureFlags.new_webpt_soap2_integration === true,
        featureFlags.webpt2_chrome_freeze_fix === true
      );
    }
    return _cachedWebPTSoap2Injector ?? WebPTSoap2Inject;
  }
  if (key === 'app.webpt.com') return _cachedWebPTInjector ?? pickWebPTInjector();
  const raw = injectors[key];
  return Array.isArray(raw) ? raw[0] : raw;
}

const messagesFromReactAppListener = (
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) => {
  // Runtime messages reach every extension context. A routed log is terminal;
  // treating it as an injector command can generate and route another log.
  if (
    (msg as any).type === 'CONSOLE_ERROR' ||
    (msg as any).type === 'SCREEN_RECORDING_PROCESSING'
  )
    return false;

  setupConsoleLogging();

  // Check and call any of the functions we don't need an injector for
  if ((msg as any).type === 'SET_FEATURE_FLAGS') {
    featureFlagsPushed = true;
    updateFeatureFlags((msg as any).msg?.features ?? {});
    sendResponse({ result: 'ok' } as any);
    return true;
  } else if (msg.type === 'GET_HAVE_INJECTOR') {
    haveInjector(sendResponse);
    return true;
  } else if (msg.type === 'FILL_ACTIVE_FIELD') {
    fillActiveField(msg, sendResponse);
    return true;
  } else if (msg.type === 'START_SCREEN_CAPTURE') {
    startScreenCapture(msg, sendResponse);
    return true;
  } else if (msg.type === 'GET_SESSION_DATA') {
    getSessionData(msg, sender, sendResponse);
    return true;
  } else if (msg.type === 'GET_EMR_REPLAY_EVENTS') {
    const injectorKey = lookupInjectorKey();
    if (injectorKey) {
      const fresh = startEmrReplayRecording();
      // A recording that just started took its own opening snapshot.
      if (msg.args?.checkout === true && !fresh) requestEmrReplayCheckout();
    }
    const batch = drainEmrReplayEvents();
    sendResponse({
      result: {
        ...batch,
        events: batch.events,
        meta: {
          ...batch.meta,
          injectorKey,
          ...(!injectorKey ? { notRecordingReason: 'unsupported_page' } : {})
        }
      }
    });
    return true;
  } else if (msg.type === 'GET_PAGE_URL') {
    getPageUrl(sendResponse);
    return true;
  } else if ((msg as any).type === 'INJECT_COMPLIANCE_BADGE') {
    ClincientInject.injectComplianceBadge((msg as any).args?.grade ?? null, (msg as any).args?.reasons ?? [], (msg as any).args?.autoShow ?? false, (msg as any).args?.signOffGated ?? false);
    sendResponse({ result: 'ok' } as any);
    return true;
  } else if ((msg as any).type === 'REMOVE_COMPLIANCE_BADGE') {
    ClincientInject.removeComplianceBadge();
    sendResponse({ result: 'ok' } as any);
    return true;
  }

  // Before we do anything, check if we have an injector for this URL
  var foundInjector = lookupInjectorKey();
  if (!foundInjector) {
    console.log('No injector found for this URL');
    const response: ErrorDOMResponse = {
      error: 'No Injector Found'
    };
    sendResponse(response);
    return false;
  }
  (async () => {
    var injector = (injectors as Record<string, any>)[foundInjector];

    if (
      foundInjector === 'sandbox.raintreeinc.com' ||
      foundInjector === 'raintreeinc.com'
    ) {
      if (
        msg.msg !== undefined &&
        'newRaintreeIntegration' in (msg.msg ?? {})
      ) {
        _cachedRaintreeInjector = msg.msg['newRaintreeIntegration']
          ? NewRaintreeInject
          : RaintreeInject;
      }
      injector = _cachedRaintreeInjector ?? RaintreeInject;
    }

    if (foundInjector === 'emr.webpt.com') {
      if (
        msg.msg !== undefined &&
        ('newWebPTSoap2Integration' in (msg.msg ?? {}) ||
          'webpt2ChromeFreezeFix' in (msg.msg ?? {}))
      ) {
        _cachedWebPTSoap2Injector = pickWebPTSoap2Injector(
          msg.msg['newWebPTSoap2Integration'] === true,
          msg.msg['webpt2ChromeFreezeFix'] === true
        );
      }
      injector = _cachedWebPTSoap2Injector ?? WebPTSoap2Inject;
    }

    if (foundInjector === 'app.webpt.com') {
      if (msg.type === 'GET_DOM' || !_cachedWebPTInjector) {
        _cachedWebPTInjector = pickWebPTInjector();
      }
      injector = _cachedWebPTInjector;
    }

    if (foundInjector === 'go.promptemr.com') {
      if (msg.msg !== undefined && 'newPromptIntegration' in (msg.msg ?? {})) {
        _cachedPromptInjector = msg.msg['newPromptIntegration']
          ? NewPromptInject
          : PromptInject;
      }
      injector = _cachedPromptInjector ?? PromptInject;
    }

    if (foundInjector === 'corahealth.com') {
      if (msg.msg !== undefined && 'coraRom' in (msg.msg ?? {})) {
        _cachedCoraInjector = msg.msg['coraRom']
          ? NewCoraHealthInject
          : CoraHealthInject;
      }
      injector = _cachedCoraInjector ?? CoraHealthInject;
    }

    if (typeof window !== 'undefined') {
      window.currentInjector = injector;
      console.log('Exposed injector globally for direct access');
    }

    // Now that we have an injector, decide what function to run based on the message type
    console.log(`message received from ${msg.type}`);
    if (msg.type === 'GET_DOM') {
      // console.log("GET_DOM message received")
      scrapeEMR(injector, msg, sender, sendResponse);
    } else if (msg.type === 'PUSH_ANSWER') {
      // console.log("PUSH_ANSWER message received")
      answerQuestionInEMR(injector, msg, sender, sendResponse);
    } else if (msg.type === 'GET_NOTE_IDENTITY') {
      const result = await captureNoteIdentity(() => injector);
      sendResponse({ result });
    } else if (msg.type === 'FREEZE_INITIAL_NOTE') {
      const inExpectedNote = await verifyExpectedFingerprint(
        injector,
        msg.args['__expectedFingerprint']
      );
      const result =
        inExpectedNote &&
        (await freezeInitialNote(
          foundInjector,
          msg.args['__expectedNoteKey']
        ));
      sendResponse({ result });
    } else if (msg.type === 'LINK_NOTE_TO_CONVERSATION') {
      const result = await linkNoteToConversation(
        () => injector,
        foundInjector,
        msg.args['conversationId'],
        msg.args['configId'],
        msg.args['generatedAnswers'],
        msg.args['expectedNoteKey']
      );
      sendResponse({ result });
    } else if (msg.type === 'PAGE_INFO_REQUEST') {
      // console.log("PAGE_INFO_REQUEST message received")
      getPageInfo(injector, msg, sender, sendResponse);
    } else if (msg.type === 'PAGE_METADATA_REQUEST') {
      getPageMetadata(injector, msg, sender, sendResponse);
    } else if (msg.type === 'PATIENT_INFO_REQUEST') {
      // console.log("PATIENT_INFO_REQUEST message received")
      getPatientInfo(injector, msg, sender, sendResponse);
    } else if (msg.type === 'SCHEDULE_REQUEST') {
      getSchedule(injector, msg, sender, sendResponse);
    } else if (msg.type === 'GET_LOG') {
      // console.log("GET_LOG message received")
      getLogFromInjector(injector, msg, sender, sendResponse);
    } else if (msg.type === 'HIGHLIGHT_FIELDS') {
      // console.log("HIGHLIGHT_FIELDS message received")
      highlightFields(injector, msg, sender, sendResponse);
    }
  })();
  return true;
};

async function scrapeEMR(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: QuestionsResponse) => void
) {
  // Session-scoped GET_DOM includes an expected fingerprint. Verify before
  // reading note fields so Comprehend cannot scrape a different visit after
  // the original tab navigates. Legacy GET_DOM has no fingerprint and proceeds.
  const expectedFingerprint = msg.msg?.['__expectedFingerprint'];
  if (!(await verifyExpectedFingerprint(injector, expectedFingerprint))) {
    sendResponse({ result: STALE_CONTEXT_MESSAGE } as any);
    return;
  }

  // Run the injector to get the questions
  const scrapeArgs = [
    msg.msg['isWebPTUser'],
    msg.msg['dropdownCache'],
    msg.msg['replyType'],
    msg.msg['empowerNewVersion'],
    msg.msg['targetTab']
  ];
  // Comprise clinical sections are on for everyone (COM-475); passing the
  // per-user flag here would override the injector default with false.
  if (injector === PTEverywhereInject) {
    scrapeArgs.push(featureFlags.pte_custom_note_types === true);
  }
  var result = await injector.getQuestions(...scrapeArgs);

  var qs = Array.isArray(result) ? result : result?.questions || [];
  var dropdownCache = result?.dropdownCache || {};
  var dropdownMisses = result?.dropdownMisses || {};

  try {
    // Verify that all keys and questions have been trimmed. Qs is a list of dictionaries where there will generally be a "Key" and "Question" field
    for (var i = 0; i < qs.length; i++) {
      qs[i]['Key'] = qs[i]['Key'].trim();
      qs[i]['Question'] = qs[i]['Question'].trim();
    }
  } catch (e) {
    console.log('Error: ', e);
  }

  // Send the response back to the React App Chrome Extension
  const response: QuestionsResponse = {
    questions: qs,
    dropdownCache: dropdownCache,
    dropdownMisses: dropdownMisses
  };
  console.log('Sending response');
  console.log(response);
  sendResponse(response);
}

async function readCurrentFingerprint(
  injector
): Promise<{ fingerprint: string; patientInfo: any; pageInfo: any } | null> {
  if (!injector?.getPatientInfo || !injector?.getPageInfo) {
    return null;
  }
  try {
    let patientInfo = injector.getPatientInfo();
    if (patientInfo instanceof Promise) patientInfo = await patientInfo;
    let pageInfo = injector.getPageInfo();
    if (pageInfo instanceof Promise) pageInfo = await pageInfo;
    const fingerprint = [
      patientInfo?.['EMR ID'] || '',
      patientInfo?.['DOB'] || '',
      pageInfo?.['URL'] || '',
      pageInfo?.['Note Type'] || ''
    ].join('|');
    return { fingerprint, patientInfo, pageInfo };
  } catch (e) {
    console.error('[EMRSession] Fingerprint read failed:', e);
    return null;
  }
}

async function verifyExpectedFingerprint(
  injector,
  expectedFingerprint
): Promise<boolean> {
  // No expected fingerprint means this is a legacy active-tab call, not a
  // Comprehend session call.
  if (!expectedFingerprint) {
    return true;
  }

  // If the content script cannot verify the current patient/page, fail closed.
  // The side panel will stop the session and show the stale-context toast.
  const current = await readCurrentFingerprint(injector);
  if (!current) {
    return false;
  }

  if (current.fingerprint !== expectedFingerprint) {
    return false;
  }

  return true;
}

const answerQuestionInEMR = async (
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) => {
  /*
  Description: Fill out the form using the provided answers
  Args:
    question: The question to answer
    answer: The answer to the question
  Return: Results, success otherwise perhaps the answer didn't match the questions expected or whatnot...
  */
  const expectedFingerprint = msg.args?.['__expectedFingerprint'];
  // For session-scoped writes, verify immediately before mutating the DOM.
  // This is the last safe point to catch same-tab navigation.
  if (!(await verifyExpectedFingerprint(injector, expectedFingerprint))) {
    sendResponse({ result: STALE_CONTEXT_MESSAGE as any });
    return;
  }

  // console.log("Answering machine picked up the phone and needs to know what to say...")
  // Got the injector! Now run it
  const expectedNoteKey = msg.args['__expectedNoteKey'];
  if (expectedNoteKey && injector.getNoteKeyForCapture) {
    const currentNoteKey = await Promise.resolve(
      injector.getNoteKeyForCapture()
    );
    if (currentNoteKey !== expectedNoteKey) {
      sendResponse({ result: STALE_CONTEXT_MESSAGE });
      return;
    }
  }
  let rsp = await injector.answerQuestion(
    msg.args['Question'],
    msg.args['Answer'],
    msg.args['Reply Type']
  );
  const response: GenericDOMResponse = {
    result: rsp
  };
  sendResponse(response);
};

async function getPageInfo(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  try {
    // Run the injector to get the page Info
    var rsp = injector.getPageInfo();

    // If rsp is a promise, wait for it else continue
    if (rsp instanceof Promise) {
      rsp = await rsp;
    }
    rsp = normalizePlaygroundPageInfo(rsp, document.URL, lookupInjectorKey());

    console.log('Page Info');
    console.log(rsp);
    const response: GenericDOMResponse = {
      result: rsp
    };
    sendResponse(response);
  } catch (error) {
    console.error('Error in getPageInfo:', error);
    // Return a default response so the alert can show quickly
    const response: GenericDOMResponse = {
      result: {
        URL: '',
        'Note Type': ''
      }
    };
    sendResponse(response);
  }
}

async function getPageMetadata(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  /*
  Description: Return the EMR's own machine readable note metadata, if it
    publishes any. CORA Sapphire embeds a noteMetadata JSON blob carrying
    isMC / isWC / isPeds, which decides which compliance rule set grades the
    note. Injectors that expose getPageInfoForCapture already parse it.
  Return: { Metadata: {...} | null } -- always an object so callers can read
    `.Metadata` without guarding, even on EMRs that publish nothing.
  */
  try {
    let metadata: any = null;

    if (typeof injector?.getPageInfoForCapture === 'function') {
      let pageInfo = injector.getPageInfoForCapture();
      if (pageInfo instanceof Promise) {
        pageInfo = await pageInfo;
      }
      metadata = pageInfo?.['Metadata'] ?? null;
    }

    // Only structured metadata is useful downstream. Some injectors put a
    // free-text string on patient info under the same name; ignore that here.
    if (metadata !== null && typeof metadata !== 'object') {
      metadata = null;
    }

    sendResponse({ result: { Metadata: metadata } });
  } catch (error) {
    console.error('Error in getPageMetadata:', error);
    sendResponse({ result: { Metadata: null } });
  }
}

async function getPatientInfo(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  // Run the injector to get the patient Info
  var rsp = injector.getPatientInfo();
  // If rsp is a promise, wait for it else continue
  if (rsp instanceof Promise) {
    rsp = await rsp;
  }
  console.log(rsp);
  const response: GenericDOMResponse = {
    result: rsp
  };
  sendResponse(response);
}

async function getSchedule(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  try {
    if (!injector.getSchedule) {
      throw new Error('Schedule sync is not available for the current EMR.');
    }

    var rsp = injector.getSchedule(msg.args ?? {});
    if (rsp instanceof Promise) {
      rsp = await rsp;
    }
    const response: GenericDOMResponse = {
      result: rsp
    };
    sendResponse(response);
  } catch (error) {
    const response: ErrorDOMResponse = {
      error: error instanceof Error ? error.message : String(error)
    };
    sendResponse(response);
  }
}

async function getLogFromInjector(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  // Run the injector to get the page Info
  var rsp = injector.getLogFromInjector();

  // If rsp is a promise, wait for it else continue
  if (rsp instanceof Promise) {
    rsp = await rsp;
  }

  console.log('getLogFromInjector');
  console.log(rsp);
  const response: GenericDOMResponse = {
    result: rsp
  };
  console.log(response);
  sendResponse(response);
}

async function getSessionData(
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  let sessionData = {};
  if (msg.args['login']) {
    //on login
    sessionData = {
      sessionKey: localStorage.getItem('sessionKey'),
      sessionEmail: localStorage.getItem('sessionEmail')
    };
  } else {
    //on logout
    localStorage.removeItem('sessionKey');
    localStorage.removeItem('sessionEmail');
    sessionStorage.removeItem('sessionKey');
    sessionStorage.removeItem('sessionEmail');
  }

  const response: GenericDOMResponse = {
    result: { sessionData: sessionData }
  };
  sendResponse(response);
}

async function startScreenCapture(
  msg: DOMMessage,
  sendResponse: (response: GenericDOMResponse) => void
) {
  // Run the injector to get the page Info
  startScreenRecorder(msg.args['uploadUrl'], sendResponse);
  return true;
}

let recorder;
let chunks = [];

// Function to start screen capture
// Recording stopped; the S3 upload below can take ~45s, so tell the app to
// show a processing state until the preview arrives.
function notifyRecordingProcessing() {
  try {
    const msg = { type: 'SCREEN_RECORDING_PROCESSING' };
    if (window.ComprehendEmbed?.send) {
      window.ComprehendEmbed.send('SCREEN_RECORDING_PROCESSING', {});
    } else if (typeof chrome !== 'undefined' && chrome.runtime) {
      chrome.runtime.sendMessage(msg);
    }
  } catch (e) {
    /* never break the recording flow */
  }
}

async function startScreenRecorder(uploadUrl, sendResponse) {
  try {
    // Request screen capture
    const displayStream = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always' }
    });

    // Request audio capture (optional)
    let audioStream: MediaStream | null = null;
    try {
      audioStream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
    } catch (audioError) {
      console.warn('Audio capture failed or was denied:', audioError);
      // Proceed without audio if user denies or it's unsupported
    }

    // Combine video and audio streams
    const combinedStream = new MediaStream([
      ...displayStream.getVideoTracks(),
      ...(audioStream ? audioStream.getAudioTracks() : [])
    ]);

    // Initialize MediaRecorder with supported MIME type
    const mimeTypes = [
      'video/webm; codecs=vp9',
      'video/webm; codecs=vp8',
      'video/webm'
    ];
    let selectedMimeType = '';
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        selectedMimeType = type;
        break;
      }
    }
    if (!selectedMimeType) {
      throw new Error('No supported MIME type found for MediaRecorder.');
    }

    recorder = new MediaRecorder(combinedStream, {
      mimeType: selectedMimeType
    });
    chunks = []; // Reset chunks

    // Handle data available event
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      } else {
        console.error('Received empty chunk.');
      }
    };

    // Handle stop event
    recorder.onstop = async () => {
      notifyRecordingProcessing();
      const videoblob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(videoblob);

      // Upload to S3 first, then send response so the video is available when Send is clicked
      try {
        await uploadVideoBlob(uploadUrl, videoblob);
      } catch (uploadError) {
        console.error('Video upload failed:', uploadError);
      }

      const response: GenericDOMResponse = { result: { url: url } };
      sendResponse(response);

      recorder.stream.getTracks().forEach((track) => track.stop());
      chunks = [];
      recorder = null;
    };

    // Start recording
    recorder.start();

    // Listen for track ending (e.g., user stops sharing)
    combinedStream.getTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        console.log('Track ended, stopping recording...');
        stopScreenCapture();
      });
    });
    return true;
  } catch (error) {
    console.error('Error starting screen capture:', error);
  }
}

export async function uploadVideoBlob(uploadUrl, videoBlob) {
  if (!uploadUrl) {
    console.error('No upload URL provided.');
    throw new Error('Upload URL is required to upload the video.');
  }

  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT', // or 'POST' based on the API's requirement
      headers: {
        'Content-Type': 'video/webm' // Adjust if necessary
        // Add any other required headers
      },
      body: videoBlob
    });

    if (!response.ok) {
      throw new Error('Failed to upload video');
    }

    return true;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
}

// Function to stop screen capture
function stopScreenCapture() {
  if (recorder && recorder.state === 'recording') {
    console.log('Stopping the recorder...');
    recorder.stop(); // This will trigger recorder.onstop
  } else {
    console.warn('No active recorder to stop.');
  }
}

function getPageUrl(sendResponse: (response: GenericDOMResponse) => void) {
  const response: GenericDOMResponse = {
    result: {
      url: normalizePlaygroundPageUrl(document.URL, lookupInjectorKey())
    }
  };
  console.log('Page URL:', response);
  sendResponse(response);
}

function haveInjector(sendResponse: (response: GenericDOMResponse) => void) {
  let rsp = !!lookupInjectorKey();
  const response: GenericDOMResponse = {
    result: rsp
  };
  console.log(response);
  sendResponse(response);
}

function fillActiveField(
  msg: DOMMessage,
  sendResponse: (response: GenericDOMResponse) => void
) {
  console.log('Filling Active Field');
  // Get the value from the message
  var value = msg.args['value'];
  console.log('Value: ', value);
  // Get the currently active element
  var activeElement = document.activeElement;

  //
  const warning =
    'The active element is not an input or textarea. Select an area where you can type text and try again.';

  // Check if the active element is a valid editable element and not a high-level DOM element
  const tagName = activeElement.tagName;
  if (tagName === 'BODY' || tagName === 'HTML') {
    alert(warning);
  }
  // Check if the active element is an input or textarea
  else if (
    activeElement.tagName === 'INPUT' ||
    activeElement.tagName === 'TEXTAREA'
  ) {
    eventBasedFill(activeElement, value);
  }
  // Check if the active element is a contenteditable element
  else if (activeElement.isContentEditable) {
    activeElement.innerText = value;
  }
  // For other elements, set the text content
  else {
    // Alert the user that the active element is not an input or textarea
    alert(warning);
    // We don't want to do this because the active element is likely not something we should be tying in anyways;
    // activeElement.textContent = value;
  }

  // Send a response back to the React App Chrome Extension
  const response: GenericDOMResponse = { result: 'Success' };
  sendResponse(response);
}

function eventBasedFill(element, value) {
  // Focus on the element
  element.focus();

  // Click the element
  element.click();

  // Set the new value
  element.value = value;

  // Create and dispatch input event
  const inputEvent = new Event('input', { bubbles: true });
  element.dispatchEvent(inputEvent);

  // Create and dispatch change event
  const changeEvent = new Event('change', { bubbles: true });
  element.dispatchEvent(changeEvent);

  // Blur the element
  element.blur();

  // Create and dispatch blur event
  const blurEvent = new Event('blur', { bubbles: true });
  element.dispatchEvent(blurEvent);
}

async function highlightFields(
  injector,
  msg: DOMMessage,
  sender: chrome.runtime.MessageSender,
  sendResponse: (response: GenericDOMResponse) => void
) {
  // Run the injector to get the page Info
  var rsp = injector.highlightFields();

  // If rsp is a promise, wait for it else continue
  if (rsp instanceof Promise) {
    rsp = await rsp;
  }

  console.log('highlightFields');
  console.log(rsp);
  const response: GenericDOMResponse = {
    result: rsp
  };
  console.log(response);
  sendResponse(response);
}

// Development keeps the browser console unchanged. Production routes console
// output to diagnostics without exposing injector details in the page console.
let consoleLoggingInstalled = false;

function setupConsoleLogging() {
  if (
    consoleLoggingInstalled ||
    process.env.REACT_APP_SUPPRESS_INJECTOR_LOGS !== 'true'
  ) {
    return;
  }
  consoleLoggingInstalled = true;

  console.log = function (...args) {
    sendConsoleLog('log', args);
  };

  console.error = function (...args) {
    sendConsoleLog('error', args);
  };

  console.warn = function (...args) {
    sendConsoleLog('warn', args);
  };

  console.info = function (...args) {
    sendConsoleLog('info', args);
  };

  // Define a function to send console logs
  function sendConsoleLog(type, args) {
    try {
      const log = { type, message: args.join(' ') };
      if (window.ComprehendEmbed?.send) {
        window.ComprehendEmbed.send('CONSOLE_ERROR', log);
      } else if (typeof chrome !== 'undefined' && chrome.runtime) {
        chrome.runtime.sendMessage({ type: 'CONSOLE_ERROR', log });
      }
    } catch (e) {
      /* logging must never break an injector */
    }
  }
}

setupConsoleLogging();

/**
 * Fired when a message is sent from either an extension process or a content script.
 */
// In a page's MAIN world (the embed injects this bundle into the page) chrome.runtime
// can exist without onMessage — only extension contexts have it. Guard, don't throw.
if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.onMessage) {
  chrome.runtime.onMessage.addListener(messagesFromReactAppListener);
} else {
  console.log(
    'No chrome runtime available, running locally... you better be calling these yourself!'
  );
}
console.log('Injecting Comprehend PT Code');

const RECORDING_TITLE_PREFIX = '🔴 ';
let titleObserver: MutationObserver | null = null;

function ensureRecordingTitlePrefix() {
  if (!document.title.startsWith(RECORDING_TITLE_PREFIX)) {
    document.title = RECORDING_TITLE_PREFIX + document.title;
  }
}

function startRecordingTitleIndicator() {
  ensureRecordingTitlePrefix();

  const titleEl = document.querySelector('title');
  if (titleObserver) titleObserver.disconnect();
  titleObserver = new MutationObserver(() => ensureRecordingTitlePrefix());
  if (titleEl) {
    titleObserver.observe(titleEl, {
      childList: true,
      characterData: true,
      subtree: true
    });
  } else if (document.head) {
    titleObserver.observe(document.head, { childList: true, subtree: true });
  }
}

function stopRecordingTitleIndicator() {
  if (titleObserver) {
    titleObserver.disconnect();
    titleObserver = null;
  }
  if (document.title.startsWith(RECORDING_TITLE_PREFIX)) {
    document.title = document.title.slice(RECORDING_TITLE_PREFIX.length);
  }
}

if (typeof chrome !== 'undefined' && chrome.runtime) {
  chrome.runtime.onMessage.addListener((request, _sender, sendResponse) => {
    if (request?.type === 'RECORDING_INDICATOR_ON') {
      startRecordingTitleIndicator();
      sendResponse({ success: true });
      return true;
    }

    if (request?.type === 'RECORDING_INDICATOR_OFF') {
      stopRecordingTitleIndicator();
      sendResponse({ success: true });
      return true;
    }

    return false;
  });
}

// Local runner to reuse DOMEvaluator logic when chrome runtime is unavailable
export function runDOMEvaluatorLocally(msg: DOMMessage) {
  return new Promise((resolve, reject) => {
    try {
      const handled = messagesFromReactAppListener(
        msg,
        {} as chrome.runtime.MessageSender,
        (response: any) => resolve(response)
      );
      // If the handler returns false (no injector found), mirror that response
      if (handled === false) {
        resolve({ error: 'No Injector Found' });
      }
    } catch (err) {
      reject(err);
    }
  });
}

function updateFeatureFlags(features: Record<string, any>) {
  featureFlags = features;
  const emrKey = lookupInjectorKey();
  const isRaintree = emrKey === 'sandbox.raintreeinc.com' || emrKey === 'raintreeinc.com';
  const captureEnabled = featureFlags.get_all_notes === true &&
    (!isRaintree || featureFlags.raintree_note_capture === true);
  setNoteCaptureEnabled(captureEnabled);
  if (captureEnabled) {
    initNoteCapture(getInjector, emrKey);
  }
  // Injectors that capture on focus changes instead of NoteCapture's poll.
  // Prompt's capture must key the note like the integration the panel uses.
  const captureInjector: any =
    lookupInjectorKey() === 'go.promptemr.com'
      ? featureFlags.new_prompt_integration === true
        ? NewPromptInject
        : PromptInject
      : getInjector();
  syncFocusNoteCapture(
    captureEnabled ? captureInjector?.noteFocusCapture : null,
    lookupInjectorKey()
  );
}

function requestFeatureFlags() {
  // Chrome content scripts have the storage permission. Embed builds receive
  // their feature flags through their existing push bridge
  if (
    typeof chrome === 'undefined' ||
    !chrome.runtime?.id ||
    !chrome.storage?.local
  ) {
    return;
  }

  chrome.runtime.sendMessage({ type: 'GET_FEATURE_FLAGS' }, (response) => {
    const requestFailed = Boolean(chrome.runtime.lastError);
    if (featureFlagsPushed) return;
    updateFeatureFlags(requestFailed ? {} : response?.features ?? {});
  });
}

function watchFeatureFlags() {
  if (
    typeof chrome === 'undefined' ||
    !chrome.runtime?.id ||
    !chrome.storage?.onChanged
  ) {
    return;
  }

  chrome.storage.onChanged.addListener((changes, areaName) => {
    if (areaName !== 'session' || !(EXTENSION_SESSION_KEY in changes)) return;
    featureFlagsPushed = true;
    updateFeatureFlags(
      changes[EXTENSION_SESSION_KEY]?.newValue?.features ?? {}
    );
  });
}

initEMRBanner(lookupInjectorKey);
if (lookupInjectorKey() === 'kinnser.net') {
  KinserWellskyInject.initKinserViewportFit();
}
if (lookupInjectorKey() === 'hellonote.com') {
  HelloNoteInject.initHelloNoteZoom();
}
watchFeatureFlags();
requestFeatureFlags();
// The docked compliance rail (COM-381, ADR-001). No-ops for accounts it isn't
// enabled for -- see initComplianceRail.
void initComplianceRail(getInjector, lookupInjectorKey);

/** cx_embed loader.js mounts the embed's rail through this bundle export. */
export function mountComplianceRailForEmbed(host: RailHost): HTMLElement | null {
  return mountEmbedComplianceRail(getInjector, host);
}
